import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getIconFallbackLabel } from '../../lib/uiText';
import styles from './Location.module.css';

const Location = ({ data }) => {
  const { locale } = useGlobal();
  const cardRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const cardEl = cardRef.current;
    if (!cardEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const titleEl = cardEl.querySelector(`.${styles.location__title}`);
    const descEl = cardEl.querySelector(`.${styles.location__description}`);
    const iconEl = cardEl.querySelector(`.${styles.location__icon}`);
    const elements = [iconEl, titleEl, descEl].filter(Boolean);

    gsap.set(elements, { opacity: 0, y: 20 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;

          gsap.to(elements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.12,
            ease: 'power3.out',
          });

          observer.disconnect();
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(cardEl);
    return () => observer.disconnect();
  }, []);

  if (!data) return null;

  const iconMedia = data.icon?.url
    ? getStrapiMediaAttributes(data.icon, {
      altFallback: data.title || getIconFallbackLabel(locale, 'location'),
      titleFallback: data.title || getIconFallbackLabel(locale, 'location'),
    })
    : null;
  const hasMap = Boolean(data.link);

  const getEmbedUrl = () => {
    if (!data.link) return null;

    if (data.link.includes('/embed?') || data.link.includes('output=embed')) {
      return data.link;
    }

    if (data.link.includes('google.com/maps') || data.link.includes('goo.gl/maps')) {
      const encoded = encodeURIComponent(data.link);
      return `https://www.google.com/maps?output=embed&q=${encoded}`;
    }

    return data.link;
  };

  return (
    <section className={`${styles.location} ${!hasMap ? styles['location--single'] : ''}`.trim()}>
      <div ref={cardRef} className={styles.location__card}>
        {iconMedia?.src && (
          <div className={styles.location__icon}>
            <img src={iconMedia.src} alt={iconMedia.alt} title={iconMedia.title} aria-hidden="true" />
          </div>
        )}
        {data.title && <h3 className={styles.location__title}>{data.title}</h3>}
        {data.description && <p className={styles.location__description}>{data.description}</p>}
      </div>

      {hasMap && (
        <div className={styles.location__map}>
          <iframe
            title={data.title || undefined}
            src={getEmbedUrl()}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      )}
    </section>
  );
};

export default Location;
